import type { ResolvedComponent } from "@inertiajs/react"
import { createInertiaApp } from "@inertiajs/react"
import { StrictMode } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"
import { initializeGoogleAnalytics } from "@/lib/google-analytics"
import MemberIndex from "@/pages/member/index"

interface BootTiming {
  inline_started_ms?: number
  initial_visibility?: string
  first_visible_ms?: number | null
}

const bootTiming = (window as Window & { __triyogaBootTiming?: BootTiming })
  .__triyogaBootTiming
const entryModuleStartedMs = performance.now()
let pageResolveStartedMs: number | null = null
let pageResolvedMs: number | null = null
let setupStartedMs: number | null = null
let renderScheduledMs: number | null = null
let renderMode: "hydrate" | "client" | null = null

const appName = import.meta.env.VITE_APP_NAME ?? "TriYoga"
const pages = import.meta.glob<{ default: ResolvedComponent }>([
  "../pages/**/*.tsx",
  "!../pages/member/index.tsx",
])
const viteAssetPathPattern = /^\/vite(?:-dev)?\//

function roundedDuration(value: number): number | null {
  if (!Number.isFinite(value) || value < 0) return null

  return Math.round(value)
}

function criticalAssetTimings() {
  return performance
    .getEntriesByType("resource")
    .filter((entry): entry is PerformanceResourceTiming => {
      const resource = entry as PerformanceResourceTiming
      if (typeof resource.initiatorType !== "string") return false

      try {
        const url = new URL(resource.name)
        return (
          url.origin === window.location.origin &&
          viteAssetPathPattern.test(url.pathname)
        )
      } catch {
        return false
      }
    })
    .sort((left, right) => right.duration - left.duration)
    .slice(0, 10)
    .map((entry) => ({
      path: new URL(entry.name).pathname,
      type: entry.initiatorType,
      start_ms: roundedDuration(entry.startTime),
      response_start_ms: roundedDuration(entry.responseStart),
      response_end_ms: roundedDuration(entry.responseEnd),
      duration_ms: roundedDuration(entry.duration),
      transfer_bytes: entry.transferSize,
      encoded_body_bytes: entry.encodedBodySize,
      decoded_body_bytes: entry.decodedBodySize,
      protocol: entry.nextHopProtocol,
    }))
}

function paintTiming(name: "first-paint" | "first-contentful-paint") {
  const entry = performance.getEntriesByName(name, "paint")[0]
  return entry ? roundedDuration(entry.startTime) : null
}

function reportSlowNavigation(appReadyMs: number) {
  if (!window.location.pathname.startsWith("/app")) return

  const send = () => {
    const entry = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined
    const legacy = performance.timing
    const navigationStart = legacy?.navigationStart ?? 0
    const ttfbMs = entry
      ? roundedDuration(entry.responseStart - entry.requestStart)
      : roundedDuration(legacy.responseStart - legacy.requestStart)
    const loadMs = entry
      ? roundedDuration(performance.now())
      : roundedDuration(performance.now() - navigationStart)

    // Report every materially slow navigation, but avoid a telemetry request for
    // routine visits. This endpoint logs timings only; it stores no user data.
    if ((ttfbMs ?? 0) < 1_000 && appReadyMs < 3_000) return

    const payload = {
      path: window.location.pathname,
      navigation_type: entry?.type ?? "navigate",
      dns_ms: entry
        ? roundedDuration(entry.domainLookupEnd - entry.domainLookupStart)
        : roundedDuration(legacy.domainLookupEnd - legacy.domainLookupStart),
      connect_ms: entry
        ? roundedDuration(entry.connectEnd - entry.connectStart)
        : roundedDuration(legacy.connectEnd - legacy.connectStart),
      ttfb_ms: ttfbMs,
      response_ms: entry
        ? roundedDuration(entry.responseEnd - entry.responseStart)
        : roundedDuration(legacy.responseEnd - legacy.responseStart),
      dom_interactive_ms: entry
        ? roundedDuration(entry.domInteractive)
        : roundedDuration(legacy.domInteractive - navigationStart),
      dom_content_loaded_ms: entry
        ? roundedDuration(entry.domContentLoadedEventEnd)
        : roundedDuration(legacy.domContentLoadedEventEnd - navigationStart),
      inline_started_ms: roundedDuration(
        bootTiming?.inline_started_ms ?? Number.NaN,
      ),
      entry_module_started_ms: roundedDuration(entryModuleStartedMs),
      page_resolve_started_ms: roundedDuration(
        pageResolveStartedMs ?? Number.NaN,
      ),
      page_resolved_ms: roundedDuration(pageResolvedMs ?? Number.NaN),
      setup_started_ms: roundedDuration(setupStartedMs ?? Number.NaN),
      render_scheduled_ms: roundedDuration(renderScheduledMs ?? Number.NaN),
      app_ready_ms: roundedDuration(appReadyMs),
      load_ms: loadMs,
      first_paint_ms: paintTiming("first-paint"),
      first_contentful_paint_ms: paintTiming("first-contentful-paint"),
      render_mode: renderMode,
      initial_visibility: bootTiming?.initial_visibility,
      first_visible_ms: roundedDuration(
        bootTiming?.first_visible_ms ?? Number.NaN,
      ),
      visibility_at_report: document.visibilityState,
      was_discarded:
        (document as Document & { wasDiscarded?: boolean }).wasDiscarded ===
        true,
      assets: criticalAssetTimings(),
    }

    void fetch("/telemetry/navigation", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => undefined)
  }

  if (document.readyState === "complete") {
    window.setTimeout(send, 0)
  } else {
    window.addEventListener("load", send, { once: true })
  }
}

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => title || appName,

  resolve: async (name) => {
    pageResolveStartedMs = performance.now()
    const resolvePage =
      name === "member/index"
        ? () => Promise.resolve({ default: MemberIndex })
        : pages[`../pages/${name}.tsx`]
    if (!resolvePage) {
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }

    const component = (await resolvePage()).default as ResolvedComponent
    pageResolvedMs = performance.now()

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    component.layout ??= [PersistentLayout]

    return component
  },

  setup({ el, App, props }) {
    setupStartedMs = performance.now()
    const cleanAssetReloadParameter = () => {
      window.requestAnimationFrame(() => {
        const appReadyMs = performance.now()
        const url = new URL(window.location.href)
        if (url.searchParams.has("_triyoga_asset_reload")) {
          url.searchParams.delete("_triyoga_asset_reload")
          window.history.replaceState(
            window.history.state,
            "",
            `${url.pathname}${url.search}${url.hash}`,
          )
        }

        reportSlowNavigation(appReadyMs)
      })
    }

    // Uncomment the following to enable SSR hydration:
    if (el.hasChildNodes()) {
      renderMode = "hydrate"
      hydrateRoot(el, <App {...props} />)
      renderScheduledMs = performance.now()
      cleanAssetReloadParameter()
      return
    }
    renderMode = "client"
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>,
    )
    renderScheduledMs = performance.now()
    cleanAssetReloadParameter()
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
  },

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// Force dark theme on load. Light theme is unfinished.
initializeTheme()
initializeGoogleAnalytics()
